import { API } from '@/config/api';
import React, { useState, useEffect } from 'react';
import { useOutletContext, useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { User, Moon, Clock, Mail, Star, Share2, Bug, Lightbulb, Download, Trash2, AlertTriangle, FileText, Shield, Scale, Crown, Bell, Archive, BookOpen, CloudUpload, Trophy, Droplet, Weight as WeightIcon, Calendar, Flame, FlaskConical, Utensils, Sparkles, Globe, Cloud } from 'lucide-react';
import { toast } from 'sonner';
import { SUPPORTED_LANGUAGES } from '@/i18n';


export default function Settings() {
  const { user } = useOutletContext();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [darkMode, setDarkMode] = useState(true);
  const [time24Hour, setTime24Hour] = useState(false);
  const [email, setEmail] = useState('');
  const [showResetDialog, setShowResetDialog] = useState(false);
  const [showBugDialog, setShowBugDialog] = useState(false);
  const [bugDescription, setBugDescription] = useState('');
  const [submittingBug, setSubmittingBug] = useState(false);
  const [streak, setStreak] = useState(null);
  const [achievements, setAchievements] = useState(null);
  const [currentLanguage, setCurrentLanguage] = useState(i18n.language || 'en');

  const handleLanguageChange = (langCode) => {
    i18n.changeLanguage(langCode);
    setCurrentLanguage(langCode);
    localStorage.setItem('peptideforge_language', langCode);
    toast.success(`Language changed to ${SUPPORTED_LANGUAGES.find(l => l.code === langCode)?.name || langCode}`);
  };

  useEffect(() => {
    const isDark = document.documentElement.classList.contains('dark');
    setDarkMode(isDark);
    fetchProfileData();
  }, []);

  const fetchProfileData = async () => {
    try {
      const [streakRes, achRes] = await Promise.all([
        fetch(`${API}/profile/streak`, { credentials: 'include' }),
        fetch(`${API}/profile/achievements`, { credentials: 'include' })
      ]);

      if (streakRes.ok) setStreak(await streakRes.json());
      if (achRes.ok) setAchievements(await achRes.json());
    } catch (error) {
      console.error('Failed to fetch profile data:', error);
    }
  };

  const handleDarkModeToggle = () => {
    const newMode = !darkMode;
    setDarkMode(newMode);
    if (newMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
    toast.success(`${newMode ? 'Dark' : 'Light'} mode enabled`);
  };

  const handleTimeFormatToggle = () => {
    setTime24Hour(!time24Hour);
    toast.success(`${!time24Hour ? '24-hour' : '12-hour'} format enabled`);
  };

  const handleSubscribe = () => {
    if (!email) {
      toast.error('Please enter your email');
      return;
    }
    toast.success('Successfully subscribed to updates!');
    setEmail('');
  };

  const handleRateApp = () => {
    toast.success('Thank you for rating PeptideForge!');
  };

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: 'PeptideForge',
        text: 'Check out PeptideForge - Professional peptide research management',
        url: window.location.origin
      });
    } else {
      navigator.clipboard.writeText(window.location.origin);
      toast.success('Link copied to clipboard!');
    }
  };

  const handleReportBug = async () => {
    if (!bugDescription.trim()) {
      toast.error('Please describe the bug');
      return;
    }

    setSubmittingBug(true);
    try {
      const response = await fetch(`${API}/support/bug-report`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ title: 'Bug Report', description: bugDescription, severity: 'medium' })
      });

      if (response.ok) {
        toast.success('Bug report submitted! You may receive 1 month of Pro free if valid.');
        setBugDescription('');
        setShowBugDialog(false);
      } else {
        toast.error('Failed to submit bug report');
      }
    } catch (error) {
      toast.error('Failed to submit bug report');
    } finally {
      setSubmittingBug(false);
    }
  };

  const handleFeatureRequest = () => {
    window.open('mailto:support@peptideforge.com?subject=Feature Request', '_blank');
    toast.info('Opening email client...');
  };

  const handleExportData = async () => {
    try {
      const response = await fetch(`${API}/logs/export`, {
        credentials: 'include'
      });
      
      if (!response.ok) throw new Error('Export failed');
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `peptideforge_export_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      
      toast.success('Data exported successfully!');
    } catch (error) {
      toast.error('Failed to export data');
    }
  };

  const handleClearCache = () => {
    if ('caches' in window) {
      caches.keys().then(names => {
        names.forEach(name => caches.delete(name));
      });
    }
    localStorage.clear();
    sessionStorage.clear();
    toast.success('Cache cleared successfully!');
  };

  const handleResetData = () => {
    toast.error('Reset functionality requires confirmation. Contact support to reset your account.');
    setShowResetDialog(false);
  };

  const isPremium = user?.subscription_status && user.subscription_status !== 'free';

  return (
    <div className="p-4 md:p-8 max-w-4xl mx-auto" data-testid="settings-page">
      <div className="mb-8">
        <h1 className="text-4xl md:text-5xl font-bold tracking-tight mb-2">
          Settings
        </h1>
        <p className="text-muted-foreground">
          Manage your account and preferences
        </p>
      </div>

      <div className="space-y-6">
        {/* Account & Sync */}
        <Card className="p-6 glass-card">
          <div className="flex items-center gap-3 mb-4">
            <User className="h-6 w-6 text-primary" />
            <h2 className="text-2xl font-bold">Account & Sync</h2>
          </div>
          <p className="text-muted-foreground mb-6">
            Sign in to sync your logs, inventory, formulas, and settings across devices.
          </p>
          
          <div className="space-y-3">
            <div className="p-4 rounded-lg bg-secondary/50 border border-border">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium">{user?.name}</p>
                  <p className="text-sm text-muted-foreground">{user?.email}</p>
                  {isPremium && (
                    <div className="flex items-center gap-1 mt-1">
                      <Crown className="h-4 w-4 text-primary" />
                      <span className="text-xs text-primary font-medium">Premium Member</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </Card>

        {/* Appearance */}
        <Card className="p-6 glass-card">
          <div className="flex items-center gap-3 mb-4">
            <Moon className="h-6 w-6 text-primary" />
            <h2 className="text-2xl font-bold">Appearance</h2>
          </div>

          <div className="space-y-4">
            <div className="flex items-center justify-between p-4 rounded-lg bg-secondary/50 border border-border">
              <div>
                <p className="font-medium">Dark Mode</p>
                <p className="text-sm text-muted-foreground">
                  {darkMode ? 'Enabled' : 'Disabled'}
                </p>
              </div>
              <Switch checked={darkMode} onCheckedChange={handleDarkModeToggle} />
            </div>

            <div className="flex items-center justify-between p-4 rounded-lg bg-secondary/50 border border-border">
              <div>
                <p className="font-medium">24-hour time</p>
                <p className="text-sm text-muted-foreground">
                  {time24Hour ? '24-hour format' : '12-hour format'}
                </p>
              </div>
              <Switch checked={time24Hour} onCheckedChange={handleTimeFormatToggle} />
            </div>

            <div className="flex items-center justify-between p-4 rounded-lg bg-secondary/50 border border-border">
              <div className="flex items-center gap-3">
                <Globe className="h-5 w-5 text-primary" />
                <div>
                  <p className="font-medium">{t('settings.language')}</p>
                  <p className="text-sm text-muted-foreground">
                    {SUPPORTED_LANGUAGES.find(l => l.code === currentLanguage)?.name || 'English'}
                  </p>
                </div>
              </div>
              <Select value={currentLanguage} onValueChange={handleLanguageChange}>
                <SelectTrigger className="w-40">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {SUPPORTED_LANGUAGES.map(lang => (
                    <SelectItem key={lang.code} value={lang.code}>
                      <span className="mr-2">{lang.flag}</span>
                      {lang.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <Link to="/sync">
              <div className="flex items-center justify-between p-4 rounded-lg bg-secondary/50 border border-border hover:border-primary/50 cursor-pointer transition-colors">
                <div className="flex items-center gap-3">
                  <Cloud className="h-5 w-5 text-primary" />
                  <div>
                    <p className="font-medium">{t('settings.dataSync')}</p>
                    <p className="text-sm text-muted-foreground">
                      Sync data across devices
                    </p>
                  </div>
                </div>
              </div>
            </Link>
          </div>
        </Card>

        {/* Stay Updated */}
        <Card className="p-6 glass-card">
          <div className="flex items-center gap-3 mb-4">
            <Mail className="h-6 w-6 text-primary" />
            <h2 className="text-2xl font-bold">Stay Updated</h2>
          </div>
          <p className="text-muted-foreground mb-4">
            Get exclusive updates and content
          </p>

          <div className="space-y-3">
            <Label htmlFor="newsletter-email">Email</Label>
            <Input
              id="newsletter-email"
              type="email"
              placeholder="Enter your email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            <Button onClick={handleSubscribe} className="w-full" variant="default">
              <Mail className="h-4 w-4 mr-2" />
              Subscribe
            </Button>
          </div>
        </Card>

        {/* Rate & Share */}
        <Card className="p-6 glass-card">
          <div className="flex items-center gap-3 mb-4">
            <Star className="h-6 w-6 text-primary" />
            <h2 className="text-2xl font-bold">Rate & Share</h2>
          </div>
          <p className="text-muted-foreground mb-6">
            Love PeptideForge? Help others discover it.
          </p>

          <div className="space-y-3">
            <Button onClick={handleRateApp} variant="outline" className="w-full">
              <Star className="h-4 w-4 mr-2" />
              Rate PeptideForge
            </Button>
            <Button onClick={handleShare} variant="outline" className="w-full">
              <Share2 className="h-4 w-4 mr-2" />
              Share with Friends
            </Button>
          </div>
        </Card>

        {/* Contact / Support */}
        <Card className="p-6 glass-card">
          <div className="flex items-center gap-3 mb-4">
            <Bug className="h-6 w-6 text-primary" />
            <h2 className="text-2xl font-bold">Contact / Support</h2>
          </div>
          <p className="text-muted-foreground mb-4">
            We are rapidly adding new features and improving the app every week. Occasionally bugs may appear, but we fix them quickly.
          </p>
          <p className="text-sm mb-6">
            <Bug className="h-4 w-4 inline mr-1 text-destructive" />
            Found a bug? Report it and get <span className="font-bold">1 month of Pro free</span>.
          </p>

          <div className="space-y-3">
            <Dialog open={showBugDialog} onOpenChange={setShowBugDialog}>
              <DialogTrigger asChild>
                <Button variant="outline" className="w-full" data-testid="report-bug-button">
                  <Bug className="h-4 w-4 mr-2" />
                  Report a Bug
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Report a Bug</DialogTitle>
                  <DialogDescription>
                    Describe the bug you found. Valid bug reports earn 1 month of Pro free!
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4">
                  <div>
                    <Label>Bug Description</Label>
                    <Textarea
                      value={bugDescription}
                      onChange={(e) => setBugDescription(e.target.value)}
                      placeholder="Please describe what happened, what you expected, and steps to reproduce..."
                      rows={5}
                    />
                  </div>
                  <Button 
                    onClick={handleReportBug} 
                    className="w-full"
                    disabled={submittingBug}
                  >
                    {submittingBug ? 'Submitting...' : 'Submit Bug Report'}
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
            <Button onClick={handleFeatureRequest} variant="outline" className="w-full">
              <Lightbulb className="h-4 w-4 mr-2" />
              Request a Feature
            </Button>
          </div>
        </Card>

        {/* Data Management */}
        <Card className="p-6 glass-card">
          <div className="flex items-center gap-3 mb-4">
            <FileText className="h-6 w-6 text-primary" />
            <h2 className="text-2xl font-bold">Data Management</h2>
          </div>
          <p className="text-muted-foreground mb-6">
            Manage your app storage and reset options.
          </p>

          <div className="space-y-3">
            <div className="flex items-center justify-between p-4 rounded-lg bg-secondary/50 border border-border">
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <p className="font-medium">Export Data (CSV)</p>
                  {!isPremium && <Crown className="h-4 w-4 text-primary" />}
                </div>
                <p className="text-sm text-muted-foreground">
                  Download your inventory, schedules, and logs
                </p>
              </div>
              <Button 
                onClick={handleExportData} 
                variant="ghost" 
                size="icon"
                disabled={!isPremium}
              >
                <Download className="h-5 w-5" />
              </Button>
            </div>

            <Button onClick={handleClearCache} variant="outline" className="w-full">
              <Trash2 className="h-4 w-4 mr-2" />
              Clear Cache
            </Button>

            <Dialog open={showResetDialog} onOpenChange={setShowResetDialog}>
              <DialogTrigger asChild>
                <Button variant="destructive" className="w-full">
                  <AlertTriangle className="h-4 w-4 mr-2" />
                  Reset All Data
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Reset All Data</DialogTitle>
                  <DialogDescription>
                    This will permanently delete all your data including logs, vials, schedules, and protocols. This action cannot be undone.
                  </DialogDescription>
                </DialogHeader>
                <div className="flex gap-3">
                  <Button variant="outline" onClick={() => setShowResetDialog(false)} className="flex-1">
                    Cancel
                  </Button>
                  <Button variant="destructive" onClick={handleResetData} className="flex-1">
                    Confirm Reset
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </Card>

        {/* Profile & Tracking */}
        <Card className="p-6 glass-card">
          <div className="flex items-center gap-3 mb-4">
            <Trophy className="h-6 w-6 text-primary" />
            <h2 className="text-2xl font-bold">Profile & Tracking</h2>
          </div>

          <div className="space-y-3">
            <Link to="/achievements">
              <Button variant="outline" className="w-full justify-between">
                <div className="flex items-center">
                  <Trophy className="h-4 w-4 mr-2" />
                  Achievements
                </div>
                {achievements && (
                  <span className="text-xs text-muted-foreground">
                    {achievements.unlocked}/{achievements.total}
                  </span>
                )}
              </Button>
            </Link>

            <Link to="/check-in">
              <Button variant="outline" className="w-full justify-between">
                <div className="flex items-center">
                  <Calendar className="h-4 w-4 mr-2" />
                  Daily Check-In
                </div>
                {streak && (
                  <div className="flex items-center gap-1">
                    <Flame className="h-4 w-4 text-destructive" />
                    <span className="text-xs text-muted-foreground">{streak.current_streak} days</span>
                  </div>
                )}
              </Button>
            </Link>

            <Link to="/water">
              <Button variant="outline" className="w-full">
                <Droplet className="h-4 w-4 mr-2" />
                Water Intake Tracker
              </Button>
            </Link>

            <Link to="/weight">
              <Button variant="outline" className="w-full">
                <WeightIcon className="h-4 w-4 mr-2" />
                Weight Tracker
              </Button>
            </Link>

            <Link to="/lab-results">
              <Button variant="outline" className="w-full">
                <FlaskConical className="h-4 w-4 mr-2" />
                Lab Results
              </Button>
            </Link>

            <Link to="/macros">
              <Button variant="outline" className="w-full">
                <Utensils className="h-4 w-4 mr-2" />
                Macro Tracker
              </Button>
            </Link>

            <Link to="/insights">
              <Button variant="outline" className="w-full justify-between">
                <div className="flex items-center">
                  <Sparkles className="h-4 w-4 mr-2" />
                  AI Insights Dashboard
                </div>
                {!isPremium && <Crown className="h-4 w-4 text-primary" />}
              </Button>
            </Link>
          </div>
        </Card>

        {/* Settings & More */}
        <Card className="p-6 glass-card">
          <div className="flex items-center gap-3 mb-4">
            <FileText className="h-6 w-6 text-primary" />
            <h2 className="text-2xl font-bold">Settings & More</h2>
          </div>

          <div className="space-y-3">
            <Link to="/notifications">
              <Button variant="outline" className="w-full">
                <Bell className="h-4 w-4 mr-2" />
                Notification Settings
              </Button>
            </Link>

            <Link to="/archived-vials">
              <Button variant="outline" className="w-full">
                <Archive className="h-4 w-4 mr-2" />
                Archived Vials
              </Button>
            </Link>

            <Link to="/resources">
              <Button variant="outline" className="w-full">
                <BookOpen className="h-4 w-4 mr-2" />
                Resources
              </Button>
            </Link>

            <Link to="/backup">
              <Button variant="outline" className="w-full">
                <CloudUpload className="h-4 w-4 mr-2" />
                Backup & Restore Data
              </Button>
            </Link>
          </div>
        </Card>

        {/* Legal & Disclaimer */}
        <Card className="p-6 glass-card">
          <div className="flex items-center gap-3 mb-4">
            <Shield className="h-6 w-6 text-primary" />
            <h2 className="text-2xl font-bold">Legal & Disclaimer</h2>
          </div>

          <div className="space-y-3">
            <Button variant="outline" className="w-full" onClick={() => window.open('/disclaimer', '_blank')}>
              <AlertTriangle className="h-4 w-4 mr-2" />
              View Full Medical Disclaimer
            </Button>
            <Button variant="outline" className="w-full" onClick={() => window.open('/privacy', '_blank')}>
              <Shield className="h-4 w-4 mr-2" />
              Privacy Policy
            </Button>
            <Button variant="outline" className="w-full" onClick={() => window.open('/terms', '_blank')}>
              <Scale className="h-4 w-4 mr-2" />
              Terms of Service
            </Button>
          </div>
        </Card>

        {/* Footer */}
        <div className="text-center text-sm text-muted-foreground py-4">
          <p>PeptideForge v1.0.0</p>
        </div>
      </div>
    </div>
  );
}
